import { useSyncExternalStore } from 'react';
import type { RawWidget } from './chat-threads';

/* Store module-level des tours de chat EN COURS, DÉCOUPLÉ du cycle de vie React. */
/* But : quitter/changer de conversation ne tue plus la génération. Le stream */
/* continue dans ce store (le serveur persiste le message à la fin), et revenir */
/* sur la conversation ré-attache l'UI au tour en cours (live-resume). */
/*  */
/* Clé d'un tour = threadId, ou 'new:<nonce>' pour une conv pas encore créée */
/* (remappée vers le vrai threadId à l'événement meta). */

export interface LiveTool {
  name: string;
  args?: Record<string, unknown>;
  refs?: { document_id: string; title?: string | null; page: number }[];
}
/** @see RawWidget — same shape, re-exported under the local name for callers. */
export type LiveWidget = RawWidget;
export interface LiveTurn {
  key: string;
  threadId: string | null;
  userText: string;
  text: string;
  tools: LiveTool[];
  widgets: LiveWidget[];
  slow?: boolean;
  status: 'streaming' | 'done' | 'error';
  error?: string;
  model?: string;
}

export interface TurnCallbacks {
  onDelta: (t: string) => void;
  onTool?: (name: string, args: Record<string, unknown>) => void;
  onToolResult?: (name: string, refs: LiveTool['refs']) => void;
  onWidget?: (w: LiveWidget) => void;
  onThread?: (threadId: string, model?: string) => void;
  onDone?: (full: string) => void;
  onError?: (e: Error) => void;
  onSlow?: () => void;
  onActive?: () => void;
}
type Runner = (cb: TurnCallbacks) => Promise<void>;

const turns = new Map<string, LiveTurn>();
const subs = new Map<string, Set<() => void>>();

function notify(key: string) { const s = subs.get(key); if (s) for (const f of [...s]) f(); }

export function getTurn(key: string): LiveTurn | undefined { return turns.get(key); }
export function clearTurn(key: string): void { if (turns.has(key)) { turns.delete(key); notify(key); } }

function subscribeTurn(key: string, f: () => void): () => void {
  let s = subs.get(key);
  if (!s) { s = new Set(); subs.set(key, s); }
  s.add(f);
  return () => { const set = subs.get(key); if (set) { set.delete(f); if (set.size === 0) subs.delete(key); } };
}

/** Hook : ré-rend quand le tour `key` change. Retourne undefined si aucun tour. */
export function useLiveTurn(key: string | null): LiveTurn | undefined {
  return useSyncExternalStore(
    (cb) => (key ? subscribeTurn(key, cb) : () => {}),
    () => (key ? turns.get(key) : undefined),
    () => undefined,
  );
}

/** Démarre un tour détaché du composant. Idempotent-safe : remplace un tour
 *  terminé de même clé. Ne PAS appeler si un tour 'streaming' existe déjà pour la clé. */
export function startTurn(key: string, userText: string, run: Runner): void {
  let current = key;
  turns.set(key, {
    key, threadId: key.startsWith('new:') ? null : key, userText,
    text: '', tools: [], widgets: [], status: 'streaming',
  });
  notify(key);
  const update = (fn: (t: LiveTurn) => LiveTurn) => {
    const prev = turns.get(current);
    if (!prev) return;
    turns.set(current, fn(prev));
    notify(current);
  };
  const cb: TurnCallbacks = {
    onDelta: (t) => update((p) => ({ ...p, text: p.text + t })),
    onTool: (name, args) => update((p) => ({ ...p, tools: [...p.tools, { name, args }] })),
    onToolResult: (name, refs) => update((p) => {
      const arr = [...p.tools];
      for (let i = arr.length - 1; i >= 0; i--) { const it = arr[i]; if (it && it.name === name && !it.refs) { arr[i] = { ...it, refs }; break; } }
      return { ...p, tools: arr };
    }),
    onWidget: (w) => update((p) => ({ ...p, widgets: [...p.widgets, w] })),
    onThread: (threadId, model) => {
      const prev = turns.get(current);
      if (!prev) return;
      const next: LiveTurn = { ...prev, ...(model ? { model } : {}) };
      if (threadId && threadId !== prev.threadId) {
        const old = current;
        next.threadId = threadId;
        if (old !== threadId) {
          next.key = threadId;
          current = threadId;
          turns.delete(old);
          turns.set(threadId, next);
          notify(old); notify(threadId);
          return;
        }
      }
      turns.set(current, next);
      notify(current);
    },
    onSlow: () => update((p) => ({ ...p, slow: true })),
    onActive: () => update((p) => (p.slow ? { ...p, slow: false } : p)),
    onDone: (full) => update((p) => ({ ...p, text: full || p.text, status: 'done' })),
    onError: (e) => update((p) => ({ ...p, status: 'error', error: e.message })),
  };
  run(cb).catch((e) => cb.onError!(e instanceof Error ? e : new Error(String(e))));
}
